import { Link } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import {
  EMPLOYMENT_TYPE_LABELS,
  EMPLOYMENT_TYPES,
  followsLineManager,
  SHIFT_DISPLAY_ZONES,
  WORK_MODE_LABELS,
  startRangeProblem,
  WORK_MODES,
  isCurrencyCode,
  toMajorUnits,
  type CurrencyCode,
  type EmploymentType,
  type JobDetail,
  type UpdateJobBasicsRequest,
  type WorkMode,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Checkbox } from '../../../components/Checkbox';
import { DatePicker } from '../../../components/DatePicker';
import { Input, Textarea } from '../../../components/Input';
import { PersonPicker } from '../../../components/PersonPicker';
import { Segmented } from '../../../components/Segmented';
import { Select, type SelectOption } from '../../../components/Select';
import { SettingFact, SettingRow } from '../../../components/SettingRow';
import { Switch } from '../../../components/Switch';
import { Tabs, type TabItem } from '../../../components/Tabs';
import { timeOption } from '../../../components/TimePicker';
import { EMPTY } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { useDepartments } from '../../organisation/api/organisation-api';
import { SalaryBand } from '../../organisation/components/SalaryBand';
import { jobFileUrl, useInterviewOptions, useJobOwners, useSaveJobStep } from '../api/jobs-api';
import { useStepAutosave } from '../hooks/use-step-autosave';
import { BASICS_TAB_DESCRIPTIONS, BASICS_TAB_LABELS, MAX_HEADCOUNT, basicsTabProblems, checkBudget, isHeadcount, type BasicsTab } from './basics-tabs';
import { FileButton } from './builder/BriefEditor';
import { useAttachJobFile, useRemoveJobFile } from './builder/job-files';
import builderStyles from './builder/builder.module.css';
import styles from './BasicsStep.module.css';
import { SalaryBudgetPanel, type BudgetFigures } from './SalaryBudgetPanel';
import { ShiftPreview } from './ShiftPreview';
import { EditedMarksRow } from '../../requisitions/components/EditedMark';
import { REQUEST_BASICS_PARTS } from './request-marks';
import type { StepProps } from './step-props';

/**
 * JOB-13's Basics (R3; JB-1…JB-3, F5.24), as one card with four tabs (Job-salary-budget mockup,
 * Redesign Group 2): The hire, People, Salary budget and More about the job, each tab showing
 * how many things on it still need fixing. A job created directly edits its title, department
 * and headcount; one made from a hire request keeps what the request decided (E3). Requested by
 * and Line manager are needed to open (D20). The hiring manager follows the line manager unless
 * someone else is chosen (DECISIONS-LOG "Start R3" Q2).
 */

type BasicsDraft = Required<Omit<UpdateJobBasicsRequest, 'version'>>;

/** Half-hour steps are how shifts are actually written; a free time box invites 18:07. Labels read
 *  "6:00 pm" (D10); the filter still finds "18:00". */
const SHIFT_TIMES: readonly SelectOption[] = Array.from({ length: 48 }, (_, index) => {
  const hours = String(Math.floor(index / 2)).padStart(2, '0');
  return timeOption(`${hours}:${index % 2 === 0 ? '00' : '30'}`);
});

/** A job with no position has no currency yet; the server saves its budget in taka (owner, 2026-10-18 Q2). */
const DEFAULT_BUDGET_CURRENCY: CurrencyCode = 'BDT';
const IMAGE_ACCEPT = '.png,.jpg,.jpeg';

/**
 * The tab each job was left on, for this browser session: leaving Basics for another section and
 * coming back opens the same tab, without an address change or a reload (CLAUDE.md §5a).
 */
const openTabs = new Map<string, BasicsTab>();

function draftOf(job: JobDetail, currency: CurrencyCode): BasicsDraft {
  return {
    title: job.title,
    departmentId: job.departmentId,
    slotsTotal: job.slotsTotal,
    requestedByUserId: job.requestedByUserId,
    lineManagerUserId: job.lineManagerUserId,
    hiringManagerUserId: job.hiringManagerUserId,
    description: job.description,
    responsibilities: job.responsibilities,
    requirements: job.requirements,
    publicTitle: job.publicTitle ?? job.title,
    employmentType: job.employmentType,
    workMode: job.workMode,
    location: job.location,
    roleFamily: job.roleFamily,
    targetStartDate: job.targetStartDate,
    targetStartLatest: job.targetStartLatest,
    targetCloseDate: job.targetCloseDate,
    recruiterUserId: job.recruiterUserId,
    isConfidential: job.isConfidential,
    managesPeople: job.managesPeople,
    shiftHours: job.shiftHours,
    shiftStart: job.shiftStart,
    shiftEnd: job.shiftEnd,
    shiftDisplayZone: job.shiftDisplayZone,
    budgetMin: wholeFromMinor(job.salaryBudget.minMinor, currency),
    budgetMax: wholeFromMinor(job.salaryBudget.maxMinor, currency),
  };
}

/** The budget as typed: whole units in the job's currency (minor units on the wire, #7). */
function wholeFromMinor(minor: string | null, currency: CurrencyCode): number | null {
  if (minor === null || !/^\d+$/.test(minor)) return null;
  return toMajorUnits(BigInt(minor), currency);
}

/** The hire on a job made from a requisition (or before Hire requests was switched off) is locked (E3). */
function lockedHireDescription(hasRequisition: boolean): string {
  return hasRequisition ? 'Decided when the hire was approved, so locked here.' : 'Set when the job was created.';
}

function digitsFrom(whole: number | null): string {
  return whole === null ? '' : String(whole);
}

export function BasicsStep({ job, isReadOnly, onConflict, footer }: StepProps): ReactNode {
  const budgetCurrency = job.salaryBudget.currency !== null && isCurrencyCode(job.salaryBudget.currency) ? job.salaryBudget.currency : DEFAULT_BUDGET_CURRENCY;
  const [draft, setDraft] = useState<BasicsDraft>(() => draftOf(job, budgetCurrency));
  // Every change builds on the latest draft, so an undo ten seconds later never puts back
  // anything else typed since.
  const latest = useRef(draft);
  // What's typed in "People needed", so the box can be cleared to type a new number; only a
  // whole number from 1 to 50 is saved.
  const [slotsText, setSlotsText] = useState(() => String(job.slotsTotal));
  const owners = useJobOwners(job.id);
  const people = useInterviewOptions(job.id).data?.people ?? [];
  const departments = useDepartments();
  // R1 (§L34): positions, grades and salary bands belong to Organisation, the requisition to Hire
  // requests; a switched-off module's facts go, and a job is then shown as the hire it is.
  const isOrganisationOn = useModule('organisation');
  const isDirect = job.requisitionId === null;
  const hasRequisition = useModule('requisitions') && !isDirect;
  const saveStep = useSaveJobStep();
  const { state, schedule, flush } = useStepAutosave<BasicsDraft>({
    job,
    onConflict,
    save: (next, version) => saveStep.mutateAsync({ id: job.id, step: 'basics', body: { ...next, version } }),
  });
  const errors = state.kind === 'error' ? state.fields : {};
  // D67 (ADR 0088): said as soon as both start dates are picked, as on the request; the server refuses it too.
  const startProblem = startRangeProblem(draft.targetStartDate, draft.targetStartLatest);

  // Owner, 2026-10-18 Q2 and 2026-10-19 Q1: the job's own optional budget. Hidden (masked) for
  // anyone who may not see salaries.
  const isBudgetVisible = (job.salaryBudget.restrictedFields ?? []).length === 0;
  const [budgetText, setBudgetText] = useState<BudgetFigures>(() => ({ min: digitsFrom(draft.budgetMin), max: digitsFrom(draft.budgetMax) }));
  const budget = checkBudget(budgetText.min, budgetText.max);

  const problems = basicsTabProblems({
    isDirect,
    title: draft.title,
    slotsText,
    requestedByUserId: draft.requestedByUserId,
    lineManagerUserId: draft.lineManagerUserId,
    recruiterUserId: draft.recruiterUserId,
    publicTitle: draft.publicTitle,
    shiftStart: draft.shiftStart,
    shiftEnd: draft.shiftEnd,
    shiftDisplayZone: draft.shiftDisplayZone,
    budgetMessage: isBudgetVisible ? budget.message : null,
    refusedFields: Object.keys(errors),
  });
  const shownTabs: readonly BasicsTab[] = isBudgetVisible ? ['hire', 'people', 'budget', 'more'] : ['hire', 'people', 'more'];
  const [pickedTab, setPickedTab] = useState<BasicsTab>(() => openTabs.get(job.id) ?? shownTabs.find((tab) => problems[tab] > 0) ?? 'hire');
  const tab = shownTabs.includes(pickedTab) ? pickedTab : 'hire';

  function openTab(next: BasicsTab): void {
    openTabs.set(job.id, next);
    setPickedTab(next);
  }

  function change(patch: Partial<BasicsDraft>): void {
    const next = { ...latest.current, ...patch };
    latest.current = next;
    setDraft(next);
    schedule(next);
  }

  /** Saved once the pair makes sense: a top below the from waits until it's fixed. */
  function changeBudget(text: BudgetFigures): void {
    setBudgetText(text);
    const checked = checkBudget(text.min, text.max);
    if (checked.message !== null) return;
    change({ budgetMin: checked.min, budgetMax: checked.max });
  }

  /** Q2: the hiring manager follows the line manager while it's empty or still the same person. */
  function chooseLineManager(lineManagerUserId: string | null): void {
    change({ lineManagerUserId, ...(followsLineManager(latest.current) ? { hiringManagerUserId: lineManagerUserId } : {}) });
  }

  const text = (value: string): string | null => (value.trim() === '' ? null : value);
  const departmentOptions = (departments.data ?? [])
    .filter((department) => department.ventureId === job.ventureId && (department.archivedAt === null || department.id === job.departmentId))
    .map((department) => ({ value: department.id, label: department.name }));
  const personOptions = people.map((person) => ({ id: person.id, fullName: person.fullName, email: person.email }));

  const tabs: readonly TabItem<BasicsTab>[] = shownTabs.map((key) => {
    const count = problems[key];
    if (count > 0) return { value: key, label: BASICS_TAB_LABELS[key], after: <span className={styles.missCount}>{count}</span>, afterLabel: `${count} to fix` };
    if (key === 'budget') return { value: key, label: BASICS_TAB_LABELS[key], after: <span className={styles.optionalWord}>optional</span> };
    return { value: key, label: BASICS_TAB_LABELS[key] };
  });
  const hireDescription = isDirect ? BASICS_TAB_DESCRIPTIONS.hire : lockedHireDescription(hasRequisition);

  const panels: Record<BasicsTab, () => ReactNode> = {
    hire: () =>
      isDirect ? (
        <>
          <SettingRow label="Job title" isRequired hint="The internal title. Candidates can see a different one." error={errors.title}>
            {({ inputId, describedBy, isInvalid }) => (
              <div className={styles.grow}>
                <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} disabled={isReadOnly} value={draft.title} maxLength={200} onChange={(event) => change({ title: event.target.value })} />
              </div>
            )}
          </SettingRow>
          <SettingRow label="Department" isRequired hint={job.ventureName} error={errors.departmentId}>
            {({ inputId, describedBy, isInvalid }) => (
              <div className={styles.grow}>
                <Select
                  id={inputId}
                  describedBy={describedBy}
                  label="Department"
                  isInvalid={isInvalid}
                  disabled={isReadOnly}
                  value={draft.departmentId}
                  options={departmentOptions}
                  onChange={(departmentId) => change({ departmentId })}
                />
              </div>
            )}
          </SettingRow>
          <SettingRow label="People needed" isRequired hint={`1 to ${MAX_HEADCOUNT}`} error={errors.slotsTotal}>
            {({ inputId, describedBy, isInvalid }) => (
              <div className={styles.short}>
                <Input
                  id={inputId}
                  aria-describedby={describedBy}
                  isInvalid={isInvalid || !isHeadcount(slotsText)}
                  isNumeric
                  inputMode="numeric"
                  disabled={isReadOnly}
                  value={slotsText}
                  onChange={(event) => {
                    setSlotsText(event.target.value);
                    if (isHeadcount(event.target.value)) change({ slotsTotal: Number(event.target.value) });
                  }}
                  onBlur={() => setSlotsText(String(latest.current.slotsTotal))}
                />
              </div>
            )}
          </SettingRow>
        </>
      ) : (
        <>
          <SettingFact label="Internal title">{job.title}</SettingFact>
          {hasRequisition && job.requisitionId && (
            <SettingFact label="Requisition">
              <Link to="/requisitions/$requisitionId" params={{ requisitionId: job.requisitionId }}>
                {job.requisitionReference}
              </Link>
            </SettingFact>
          )}
          <SettingFact label="Department">{`${job.departmentName} · ${job.ventureName}`}</SettingFact>
          {isOrganisationOn && (
            <>
              <SettingFact label="Position">{job.position ? `${job.position.code} · ${job.position.title}` : EMPTY}</SettingFact>
              <SettingFact label="Salary band">{job.position ? <SalaryBand position={job.position} /> : EMPTY}</SettingFact>
            </>
          )}
          <SettingFact label="Hires">{String(job.slotsTotal)}</SettingFact>
        </>
      ),
    people: () => (
      <>
        <SettingRow label="Requested by" isRequired hint="Gets the shortlist." error={errors.requestedByUserId}>
          {({ inputId, describedBy, isInvalid }) => (
            <div className={styles.grow}>
              <PersonPicker id={inputId} describedBy={describedBy} label="Requested by" isInvalid={isInvalid} disabled={isReadOnly} people={personOptions} value={draft.requestedByUserId} onChange={(requestedByUserId) => change({ requestedByUserId })} />
            </div>
          )}
        </SettingRow>
        <SettingRow label="Line manager" isRequired hint="Who the new hire reports to. Gets the shortlist." error={errors.lineManagerUserId}>
          {({ inputId, describedBy, isInvalid }) => (
            <div className={styles.grow}>
              <PersonPicker id={inputId} describedBy={describedBy} label="Line manager" isInvalid={isInvalid} disabled={isReadOnly} people={personOptions} value={draft.lineManagerUserId} onChange={chooseLineManager} />
            </div>
          )}
        </SettingRow>
        {/* On every job, a request-based one too: a request can arrive naming nobody (tester D1). */}
        <SettingRow label="Hiring manager" isOptional hint="Makes the decisions. The line manager unless you choose someone else." error={errors.hiringManagerUserId}>
          {({ inputId, describedBy }) => (
            <div className={styles.grow}>
              <PersonPicker
                id={inputId}
                describedBy={describedBy}
                label="Hiring manager"
                placeholder="Same as the line manager"
                isClearable
                disabled={isReadOnly}
                people={personOptions}
                value={draft.hiringManagerUserId}
                onChange={(hiringManagerUserId) => change({ hiringManagerUserId })}
              />
            </div>
          )}
        </SettingRow>
        <SettingRow label="Recruiter" isRequired hint="Runs the job day to day." error={errors.recruiterUserId}>
          {({ inputId, isInvalid }) => (
            <div className={styles.grow}>
              <Select
                id={inputId}
                label="Recruiter"
                placeholder="Choose the recruiter"
                isInvalid={isInvalid}
                disabled={isReadOnly}
                value={draft.recruiterUserId}
                options={(owners.data ?? []).map((owner) => ({ value: owner.id, label: owner.fullName }))}
                onChange={(recruiterUserId) => change({ recruiterUserId })}
              />
            </div>
          )}
        </SettingRow>
        <SettingRow label="Work mode" isGroup>
          {() => (
            <Segmented
              label="Work mode"
              disabled={isReadOnly}
              value={draft.workMode}
              options={WORK_MODES.map((mode) => ({ value: mode, label: WORK_MODE_LABELS[mode] }))}
              onChange={(workMode: WorkMode) => change({ workMode })}
            />
          )}
        </SettingRow>
        <SettingRow label="Location" isOptional error={errors.location}>
          {({ inputId, isInvalid }) => (
            <div className={styles.grow}>
              <Input id={inputId} isInvalid={isInvalid} disabled={isReadOnly} value={draft.location ?? ''} placeholder="Tejgaon, Dhaka" onChange={(event) => change({ location: text(event.target.value) })} />
            </div>
          )}
        </SettingRow>
      </>
    ),
    budget: () => (
      <SalaryBudgetPanel
        figures={budgetText}
        onChange={changeBudget}
        currency={budgetCurrency}
        isReadOnly={isReadOnly}
        // 2026-10-19 Q1 (b): the job's budget is its own; the band it started from stays locked.
        {...(isOrganisationOn && job.position !== null ? { band: <SalaryBand position={job.position} /> } : {})}
      />
    ),
    more: () => (
      <>
        <SettingRow label="Title candidates see" isRequired hint="Often different from the internal title." error={errors.publicTitle}>
          {({ inputId, describedBy, isInvalid }) => (
            <div className={styles.grow}>
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} disabled={isReadOnly} value={draft.publicTitle} onChange={(event) => change({ publicTitle: event.target.value })} />
            </div>
          )}
        </SettingRow>
        <SettingRow label="Employment type" isGroup>
          {() => (
            <Segmented
              label="Employment type"
              disabled={isReadOnly}
              value={draft.employmentType}
              options={EMPLOYMENT_TYPES.map((type) => ({ value: type, label: EMPLOYMENT_TYPE_LABELS[type] }))}
              onChange={(employmentType: EmploymentType) => change({ employmentType })}
            />
          )}
        </SettingRow>
        <SettingRow label="Role family" isOptional hint="Picks the pipeline HR set for it." error={errors.roleFamily}>
          {({ inputId, describedBy, isInvalid }) => (
            <div className={styles.grow}>
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} disabled={isReadOnly} value={draft.roleFamily ?? ''} placeholder="Finance" onChange={(event) => change({ roleFamily: text(event.target.value) })} />
            </div>
          )}
        </SettingRow>
        <SettingRow
          label="Target dates"
          isOptional
          isGroup
          hint="When the job should close, and the earliest and latest the new hire should start."
          error={errors.targetCloseDate ?? startProblem ?? errors.targetStartLatest ?? errors.targetStartDate}
        >
          {() => (
            <>
              <div className={styles.grow}>
                <DatePicker label="Target close date" placeholder="Close: choose a date" isInvalid={errors.targetCloseDate !== undefined} disabled={isReadOnly} value={draft.targetCloseDate ?? ''} onChange={(value) => change({ targetCloseDate: text(value) })} />
              </div>
              <div className={styles.grow}>
                <DatePicker
                  label="Earliest start date"
                  placeholder="Earliest start: choose a date"
                  isInvalid={errors.targetStartDate !== undefined}
                  disabled={isReadOnly}
                  value={draft.targetStartDate ?? ''}
                  {...(draft.targetStartLatest ? { max: draft.targetStartLatest } : {})}
                  onChange={(value) => change({ targetStartDate: text(value) })}
                />
              </div>
              <div className={styles.grow}>
                <DatePicker
                  label="Latest start date"
                  placeholder="Latest start: choose a date"
                  isInvalid={startProblem !== null || errors.targetStartLatest !== undefined}
                  disabled={isReadOnly}
                  value={draft.targetStartLatest ?? ''}
                  {...(draft.targetStartDate ? { min: draft.targetStartDate } : {})}
                  onChange={(value) => change({ targetStartLatest: text(value) })}
                />
              </div>
            </>
          )}
        </SettingRow>
        <SettingRow label="Confidential" isGroup>
          {() => (
            <Checkbox checked={draft.isConfidential} disabled={isReadOnly} label="Keep this job off the career site and out of other people's lists" onChange={(isConfidential) => change({ isConfidential })} />
          )}
        </SettingRow>
        {/* ADR 0083 (owner 2026-11-01 Q6 a): the Leadership add-on's parts count only while this is on. */}
        <SettingRow label="This role manages people" isGroup hint="Adds the Leadership add-on to the ideal candidate and to each hire's 30 and 90-day plans.">
          {() => <Switch checked={draft.managesPeople} disabled={isReadOnly} label={draft.managesPeople ? 'Yes, they will manage a team' : 'No'} onChange={(managesPeople) => change({ managesPeople })} />}
        </SettingRow>
        <SettingRow
          label="Shift"
          isOptional
          isGroup
          hint="In BD time, with the client's time zone if it differs."
          error={errors.shiftEnd ?? errors.shiftDisplayZone ?? errors.shiftStart}
        >
          {() => (
            <>
              <div className={styles.grow}>
                <Select
                  label="Shift start, BD time"
                  placeholder="No set shift"
                  disabled={isReadOnly}
                  value={draft.shiftStart}
                  options={[{ value: '', label: 'No set shift' }, ...SHIFT_TIMES]}
                  onChange={(value) => change({ shiftStart: text(value), ...(value === '' ? { shiftEnd: null } : {}) })}
                />
              </div>
              <div className={styles.grow}>
                <Select label="Shift end, BD time" placeholder="Ends" disabled={isReadOnly || draft.shiftStart === null} value={draft.shiftEnd} options={SHIFT_TIMES} onChange={(shiftEnd) => change({ shiftEnd })} />
              </div>
              <div className={styles.grow}>
                <Select
                  label="Client time zone"
                  placeholder="Client time zone"
                  disabled={isReadOnly || draft.shiftStart === null}
                  value={draft.shiftDisplayZone}
                  options={SHIFT_DISPLAY_ZONES.map((zone) => ({ value: zone.value, label: zone.label }))}
                  onChange={(shiftDisplayZone) => change({ shiftDisplayZone })}
                />
              </div>
              <div className={styles.full}>
                <ShiftPreview start={draft.shiftStart} end={draft.shiftEnd} zone={draft.shiftDisplayZone} />
              </div>
            </>
          )}
        </SettingRow>
        <SettingRow label="Shift note for candidates" isOptional error={errors.shiftHours}>
          {({ inputId, isInvalid }) => (
            <div className={styles.grow}>
              <Input id={inputId} isInvalid={isInvalid} disabled={isReadOnly} value={draft.shiftHours ?? ''} placeholder="Night shift, Sunday to Thursday" onChange={(event) => change({ shiftHours: text(event.target.value) })} />
            </div>
          )}
        </SettingRow>
        <AdvertImage job={job} isReadOnly={isReadOnly} />
        <SettingRow label="Advert: about the job" isOptional isWide hint="What candidates read." error={errors.description}>
          {({ inputId, describedBy, isInvalid }) => (
            <div className={styles.full}>
              <Textarea id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} rows={4} disabled={isReadOnly} value={draft.description ?? ''} placeholder="What the role is and who it suits" onChange={(event) => change({ description: text(event.target.value) })} />
            </div>
          )}
        </SettingRow>
        <SettingRow label="Advert: responsibilities" isOptional isWide error={errors.responsibilities}>
          {({ inputId, isInvalid }) => (
            <div className={styles.full}>
              <Textarea id={inputId} isInvalid={isInvalid} rows={3} disabled={isReadOnly} value={draft.responsibilities ?? ''} onChange={(event) => change({ responsibilities: text(event.target.value) })} />
            </div>
          )}
        </SettingRow>
        <SettingRow label="Advert: requirements" isOptional isWide error={errors.requirements}>
          {({ inputId, isInvalid }) => (
            <div className={styles.full}>
              <Textarea id={inputId} isInvalid={isInvalid} rows={3} disabled={isReadOnly} value={draft.requirements ?? ''} onChange={(event) => change({ requirements: text(event.target.value) })} />
            </div>
          )}
        </SettingRow>
      </>
    ),
  };

  return (
    <div className={styles.step}>
      <Card title="Basics" meta="Changes save as you go" isFlush>
        <Tabs label="Basics" tabs={tabs} value={tab} onChange={openTab}>
          <p className={styles.description}>{tab === 'hire' ? hireDescription : BASICS_TAB_DESCRIPTIONS[tab]}</p>
          <EditedMarksRow marks={job.requestEditMarks} parts={REQUEST_BASICS_PARTS} />
          {panels[tab]()}
        </Tabs>
      </Card>

      {footer({ state, flush })}
    </div>
  );
}

/** JB-3: the advert's banner. Replacing it keeps the old one on record (non-negotiable #6). */
function AdvertImage({ job, isReadOnly }: { readonly job: JobDetail; readonly isReadOnly: boolean }): ReactNode {
  const { attach, isAttaching } = useAttachJobFile(job);
  const files = useRemoveJobFile(job);
  const image = job.imageDocumentId && !files.hidden.has(job.imageDocumentId) ? job.files.find((file) => file.id === job.imageDocumentId) ?? null : null;
  // ADR 0066: with Job post image on, the advert image lives in the Job post section.
  if (useModule('job_image')) return null;
  return (
    <SettingRow label="Advert image" isOptional isGroup hint="A banner for the job advert. PNG or JPG, up to 10 MB.">
      {() => (
        <div className={builderStyles.imageRow}>
          {image && <img className={builderStyles.imagePreview} src={jobFileUrl(job.id, image.id)} alt={`Advert image: ${image.fileName}`} />}
          {!isReadOnly && (
            <FileButton label="Choose an advert image" accept={IMAGE_ACCEPT} isPending={isAttaching} onFile={(file) => void attach(file, { isImage: true })}>
              {image ? 'Replace image' : 'Add an image'}
            </FileButton>
          )}
          {image && !isReadOnly && (
            <Button variant="ghost" size="sm" icon={Trash2} onClick={() => files.remove(image.id, image.fileName)}>
              Remove
            </Button>
          )}
          {!image && isReadOnly && <span>{EMPTY}</span>}
        </div>
      )}
    </SettingRow>
  );
}
